import React from 'react';
import PropTypes from 'prop-types';
import styled from 'styled-components';
import { Field } from 'react-final-form';
import { FieldArray } from 'react-final-form-arrays';
import { Box, Flex } from 'rebass';

import { Typography, Button } from '@oca/ui';
import { Add } from '@oca/icons';
import { Trans } from '@lingui/macro';

import { FileUploaderField } from '@features/common';
import {
  DOCUMENTS,
  EDUCATION_LEVELS,
  createDocumentModel,
  createEducationModel,
} from '../model/static';
import {
  FieldsetDocument,
  FieldsetContract,
  FieldsetEducation,
  DocumentCard,
} from '../molecules';

const titles = {
  [DOCUMENTS.PASSPORT]: <Trans>Passport</Trans>,
  [DOCUMENTS.CIVIL_ID]: <Trans>Civil ID</Trans>,
  [DOCUMENTS.VISA]: <Trans>Visa</Trans>,
  [DOCUMENTS.WORK_PERM]: <Trans>Work permission</Trans>,
  [DOCUMENTS.CONTRACT]: <Trans>Employment contract</Trans>,
  [DOCUMENTS.NDA]: <Trans>NDA</Trans>,
  [DOCUMENTS.BANK_LOAN]: <Trans>Bank loan</Trans>,
  [EDUCATION_LEVELS.MASTER]: <Trans>Master</Trans>,
  [EDUCATION_LEVELS.BACHELOR]: <Trans>Bachelor</Trans>,
  [EDUCATION_LEVELS.CERTIFICATE]: <Trans>Certificate</Trans>,
};

const optionalDocumentTypes = [DOCUMENTS.VISA, DOCUMENTS.BANK_LOAN];
const uidDocumentTypes = [
  DOCUMENTS.PASSPORT,
  DOCUMENTS.CIVIL_ID,
  DOCUMENTS.BANK_LOAN,
];

const contractOptions = [
  { id: DOCUMENTS.CONTRACT, label: titles[DOCUMENTS.CONTRACT] },
  { id: DOCUMENTS.NDA, label: titles[DOCUMENTS.NDA] },
];

export function FormDocuments({ educationOptions, editable }) {
  return (
    <React.Fragment>
      <FieldArray name="documents">
        {({ fields }) => {
          const indicesByType = (fields.value || []).reduce(
            (acc, doc, i) => {
              if (!doc) return acc;
              if (doc.docType === DOCUMENTS.BANK_LOAN) acc.loans.push(i);
              else acc.identity.push(i);
              return acc;
            },
            { identity: [], loans: [] },
          );

          return (
            <React.Fragment>
              {/* Identity & work documents (fixed set per employee) */}
              <Section
                title={<Trans>Identity &amp; work documents</Trans>}
                description={
                  <Trans>
                    Required documents for the employee&apos;s file. Visa and
                    work permission are shown only for non-residents.
                  </Trans>
                }
              >
                {indicesByType.identity.map(i => (
                  <Field
                    key={fields.name + i}
                    name={`${fields.name}[${i}].docType`}
                    subscription={{ value: true }}
                    render={({ input: { value } }) => (
                      <FieldsetDocument
                        name={`${fields.name}[${i}]`}
                        title={titles[value] || value}
                        withUid={uidDocumentTypes.includes(value)}
                        isOptional={optionalDocumentTypes.includes(value)}
                      />
                    )}
                  />
                ))}
              </Section>

              {/* Bank loans (admin-added, optional) */}
              <Section
                title={<Trans>Bank loans</Trans>}
                description={
                  <Trans>
                    Needed so HR can prepare bank notifications before
                    offboarding this employee.
                  </Trans>
                }
                action={
                  editable && (
                    <AddButton
                      onClick={() =>
                        fields.push(createDocumentModel(DOCUMENTS.BANK_LOAN))
                      }
                      label={<Trans>Add bank loan</Trans>}
                    />
                  )
                }
              >
                {indicesByType.loans.length === 0 ? (
                  <EmptyState>
                    <Trans>No bank loan documents on file.</Trans>
                  </EmptyState>
                ) : (
                  indicesByType.loans.map(i => {
                    const doc = fields.value[i] || {};
                    return (
                      <FieldsetDocument
                        key={fields.name + i}
                        name={`${fields.name}[${i}]`}
                        title={titles[DOCUMENTS.BANK_LOAN]}
                        withUid
                        isOptional
                        isBankLoan
                        onRemove={
                          editable && !doc.id
                            ? () => fields.remove(i)
                            : undefined
                        }
                      />
                    );
                  })
                )}
              </Section>
            </React.Fragment>
          );
        }}
      </FieldArray>

      <FieldArray name="contracts">
        {({ fields }) => (
          <Section
            title={<Trans>Signed contracts</Trans>}
            description={
              <Trans>Employment contract and non-disclosure agreement.</Trans>
            }
            action={
              editable && (
                <AddButton
                  onClick={() =>
                    fields.push(createDocumentModel(DOCUMENTS.NDA))
                  }
                  // Only two contracts now
                  disabled={fields.value.length === 2}
                  label={<Trans>Add contract</Trans>}
                />
              )
            }
          >
            {fields.map((fieldName, index) => (
              <FieldsetContract
                key={fieldName}
                index={index + 1}
                name={fieldName}
                onDelete={index > 0 ? fields.pop : undefined}
                options={contractOptions}
              />
            ))}
          </Section>
        )}
      </FieldArray>

      <FieldArray name="curriculumVitae">
        {({ fields }) => (
          <Section
            title={<Trans>Curriculum vitae (CV)</Trans>}
            description={<Trans>One file per employee.</Trans>}
            action={
              editable && (
                <AddButton
                  disabled={fields.value.length >= 1}
                  onClick={() => fields.push(createDocumentModel(DOCUMENTS.CV))}
                  label={<Trans>Add CV</Trans>}
                />
              )
            }
          >
            {fields.value.length < 1 ? (
              <EmptyState>
                <Trans>No CV was added yet.</Trans>
              </EmptyState>
            ) : (
              fields.map(
                (name, index) =>
                  index < 1 && (
                    <DocumentCard
                      key={name}
                      title={<Trans>Curriculum vitae</Trans>}
                      onRemove={
                        editable ? () => fields.remove(index) : undefined
                      }
                    >
                      <FileUploaderField
                        name={`${name}.files`}
                        label={<Trans>Picture / scan</Trans>}
                      />
                    </DocumentCard>
                  ),
              )
            )}
          </Section>
        )}
      </FieldArray>

      <FieldArray name="educations">
        {({ fields }) => (
          <Section
            title={<Trans>Education</Trans>}
            description={<Trans>Degrees and certificates on file.</Trans>}
            action={
              editable && (
                <AddButton
                  onClick={() =>
                    fields.push(createEducationModel(EDUCATION_LEVELS.MASTER))
                  }
                  label={<Trans>Add education</Trans>}
                />
              )
            }
          >
            {fields.value.length === 0 ? (
              <EmptyState>
                <Trans>No educations were added yet.</Trans>
              </EmptyState>
            ) : (
              fields.map((field, index) => (
                <FieldsetEducation
                  key={field}
                  name={field}
                  index={index}
                  options={educationOptions}
                  onDelete={editable ? () => fields.remove(index) : undefined}
                />
              ))
            )}
          </Section>
        )}
      </FieldArray>
    </React.Fragment>
  );
}

FormDocuments.propTypes = {
  educationOptions: PropTypes.arrayOf(
    PropTypes.shape({
      value: PropTypes.string,
      name: PropTypes.oneOfType([PropTypes.string, PropTypes.node]),
    }),
  ),
  form: PropTypes.shape({ push: PropTypes.func }).isRequired,
  editable: PropTypes.bool.isRequired,
};

FormDocuments.defaultProps = {
  educationOptions: Object.values(EDUCATION_LEVELS).map(value => ({
    value,
    name: titles[value],
  })),
};

function Section({ title, description, action, children }) {
  return (
    <SectionWrap>
      <SectionHeader>
        <Box>
          <Typography fontSize={18} fontWeight={600} color="#181D27">
            {title}
          </Typography>
          {description && (
            <Typography fontSize={13} color="textSecondary" mt={1}>
              {description}
            </Typography>
          )}
        </Box>
        {action && <Box ml={3}>{action}</Box>}
      </SectionHeader>
      <Box>{children}</Box>
    </SectionWrap>
  );
}
Section.propTypes = {
  title: PropTypes.node.isRequired,
  description: PropTypes.node,
  action: PropTypes.node,
  children: PropTypes.node.isRequired,
};
Section.defaultProps = { description: null, action: null };

function AddButton({ onClick, disabled, label }) {
  return (
    <Button
      type="button"
      variant="secondary"
      size="small"
      onClick={onClick}
      disabled={disabled}
    >
      <Add />
      <span style={{ marginLeft: 6 }}>{label}</span>
    </Button>
  );
}
AddButton.propTypes = {
  onClick: PropTypes.func.isRequired,
  disabled: PropTypes.bool,
  label: PropTypes.node.isRequired,
};
AddButton.defaultProps = { disabled: false };

const SectionWrap = styled(Box)`
  margin-bottom: 20px;
  &:last-child {
    margin-bottom: 0;
  }
`;

const SectionHeader = styled(Flex)`
  align-items: flex-start;
  justify-content: space-between;
  margin-bottom: 10px;
  padding-bottom: 8px;
  border-bottom: 1px solid #eef1f4;
`;

const EmptyState = styled(Box)`
  padding: 16px;
  border: 1px dashed #d9dde3;
  border-radius: 10px;
  text-align: center;
  color: #64748b;
  font-size: 13px;
  background: #fafbfc;
`;
